import React, { useState, useEffect, useRef } from 'react';
import { feedbackService } from '../lib/supabase';

function FeedbackForm() {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    // Auto-focus the textarea when component mounts or when explicitly triggered
    const focusTextarea = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };

    // Focus on mount if already at feedback section
    if (window.location.hash === '#feedback') {
      setTimeout(focusTextarea, 100);
    }

    // Listen for custom focus event (triggered by navigation to feedback)
    const handleFocusFeedback = () => {
      setTimeout(focusTextarea, 100);
    };

    // Handle hash changes for direct navigation
    const handleHashChange = () => {
      if (window.location.hash === '#feedback') {
        setTimeout(focusTextarea, 100);
      }
    };

    window.addEventListener('focusFeedback', handleFocusFeedback);
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('focusFeedback', handleFocusFeedback);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      // Submit feedback to Supabase
      await feedbackService.create(feedback, email || null);
      
      setMessage('Thank you for your feedback! We really appreciate it.');
      setFeedback('');
      setEmail('');
    } catch (error) {
      console.error('Feedback submission error:', error);
      setMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
        Share Your Feedback
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        Help us improve Tournimate by sharing your experience, suggestions, or reporting any issues.
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
            Your Feedback
          </label>
          <textarea
            ref={textareaRef}
            id="feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Tell us about your experience with Tournimate..."
          />
        </div>
        
        <div>
          <label htmlFor="feedback-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address (optional)
          </label>
          <input
            type="email"
            id="feedback-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="your@email.com"
          />
          <p className="text-sm text-gray-500 mt-1">
            We'll only use this to follow up on your feedback if needed.
          </p>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </form>
      
      {message && (
        <div className={`mt-4 p-3 rounded-md ${
          message.includes('Thank you') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
}

export default FeedbackForm;